import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, MapPin, Users, DollarSign, ArrowLeft, Clock } from 'lucide-react';

const formatTime = (time: string) => {
  if (!time) return '';
  try {
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch { return time; }
};
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

interface TrainingSession {
  id: string;
  title: string;
  description: string;
  session_type: string;
  sport: string;
  skill_level: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  price: number;
  max_participants: number;
  current_participants: number;
  status: string;
}

const TrainingSessions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my-sessions'>('all');

  useEffect(() => {
    loadSessions();
  }, [filter, user]);

  const loadSessions = async () => {
    try {
      let query = supabase
        .from('training_sessions')
        .select('*')
        .eq('status', 'active')
        .order('date', { ascending: true });

      if (filter === 'my-sessions' && user) {
        query = query.eq('coach_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load training sessions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSessionTypeBadge = (type: string) => {
    const colors = {
      group_training: 'bg-blue-600/20 text-blue-500 border-blue-600/30',
      private_lesson: 'bg-purple-600/20 text-purple-500 border-purple-600/30',
      camp: 'bg-green-600/20 text-green-500 border-green-600/30',
      clinic: 'bg-orange-600/20 text-orange-500 border-orange-600/30',
    };
    return colors[type] || 'bg-gray-600/20 text-gray-400';
  };

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}

      <main className="flex-1 container mx-auto px-4 pt-20 py-8 pb-24 md:pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Training Sessions</h1>
            <p className="text-gray-400">Browse and book training sessions</p>
          </div>
          {user && (
            <Button
              onClick={() => navigate('/training-sessions/create')}
              className="bg-sports-green hover:bg-sports-green/80"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Session
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All Sessions
          </Button>
          {user && (
            <Button
              variant={filter === 'my-sessions' ? 'default' : 'outline'}
              onClick={() => setFilter('my-sessions')}
            >
              My Sessions
            </Button>
          )}
        </div>

        {/* Sessions Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400 mb-4">No training sessions available</p>
              {user && filter === 'all' && (
                <Button
                  onClick={() => navigate('/training-sessions/create')}
                  className="bg-sports-green hover:bg-sports-green/80"
                >
                  Create Your First Session
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <Card key={session.id} className="bg-gray-900 border-gray-700 hover:border-sports-green transition-colors">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-white text-lg">{session.title}</CardTitle>
                    <Badge className={getSessionTypeBadge(session.session_type)}>
                      {session.session_type.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="text-sports-green border-sports-green">
                      {session.sport}
                    </Badge>
                    {session.skill_level && (
                      <Badge variant="outline">{session.skill_level}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-400 line-clamp-2">{session.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Calendar className="h-4 w-4 text-sports-green" />
                      <span className="font-medium">{format(new Date(session.date), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Clock className="h-4 w-4 text-sports-green" />
                      <span className="font-semibold text-white text-base">
                        {formatTime(session.start_time)} - {formatTime(session.end_time)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin className="h-4 w-4 text-sports-green" />
                      <span className="truncate">{session.location}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-300">
                      <Users className="h-4 w-4 text-sports-green" />
                      <span>
                        {session.current_participants}/{session.max_participants || '∞'} participants
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-300">
                      <DollarSign className="h-4 w-4 text-sports-green" />
                      <span className="font-semibold text-white">
                        ${session.price}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate(`/training-sessions/${session.id}`)}
                    className="w-full mt-4"
                    variant="outline"
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default TrainingSessions;
